import { motion } from 'motion/react';
import { Loader2, Satellite, Cloud, Droplets, Thermometer, TrendingUp, Check } from 'lucide-react';

interface DetectionLoadingModalProps {
  isOpen: boolean;
  currentStep: number; // 0: start, 1: NASA API, 2: processing, 3: calculating
}

const steps = [
  {
    id: 0,
    icon: Satellite,
    title: 'Initializing Analysis',
    description: 'Preparing location coordinates and system parameters',
    detail: 'Validating GPS coordinates and climate zone detection',
  },
  {
    id: 1,
    icon: Cloud,
    title: 'Fetching NASA POWER Climate Data',
    description: 'Retrieving 4-year satellite averages (2020-2023)',
    detail: 'GHI • Temperature • Precipitation • Humidity',
  },
  {
    id: 2,
    icon: Droplets,
    title: 'Processing Climate Metrics',
    description: 'Analyzing environmental factors affecting losses',
    detail: 'Desert dust • Tropical humidity • Rainfall cleaning • Snow accumulation',
  },
  {
    id: 3,
    icon: TrendingUp,
    title: 'Calculating Optimal Losses',
    description: 'Applying NREL PVWatts & IEC 61724 standards',
    detail: 'Soiling • Shading • DC/AC losses • Temperature-sensitive degradation',
  },
];

export function DetectionLoadingModal({ isOpen, currentStep }: DetectionLoadingModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden"
      >
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#4F819F] to-[#6B9FB8] px-8 py-6 text-white">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">
                Auto-Detecting System Losses
              </h3>
              <p className="text-blue-100 text-sm">
                AI-powered analysis using real climate data
              </p>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-blue-100 mb-2">
              <span>Analysis Progress</span>
              <span className="font-semibold">{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="h-full bg-white rounded-full"
              />
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="p-8 space-y-3">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isComplete = index < currentStep;
            const isActive = index === currentStep;
            const isPending = index > currentStep;

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`
                  relative p-5 rounded-xl transition-all border-2
                  ${isActive ? 'bg-gradient-to-r from-[#4F819F]/5 to-[#6B9FB8]/5 border-[#4F819F]/40 shadow-md' : ''}
                  ${isComplete ? 'bg-green-50 border-green-300' : ''}
                  ${isPending ? 'bg-slate-50 border-slate-200' : ''}
                `}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div
                    className={`
                      w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all
                      ${isActive ? 'bg-gradient-to-br from-[#4F819F] to-[#6B9FB8] shadow-lg' : ''}
                      ${isComplete ? 'bg-green-500 shadow-md' : ''}
                      ${isPending ? 'bg-slate-300' : ''}
                    `}
                  >
                    {isComplete ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : isActive ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </motion.div>
                    ) : (
                      <Icon className="w-6 h-6 text-white" />
                    )}
                  </div>

                  {/* Text Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4
                        className={`
                          font-semibold transition-colors
                          ${isActive || isComplete ? 'text-slate-900' : 'text-slate-500'}
                        `}
                      >
                        {step.title}
                      </h4>
                      
                      {/* Status Badge */}
                      {isComplete && (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-xs font-semibold text-green-700 bg-green-100 px-2.5 py-1 rounded-full whitespace-nowrap"
                        >
                          ✓ Complete
                        </motion.span>
                      )}
                      {isActive && (
                        <motion.span
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-xs font-semibold text-[#4F819F] bg-[#4F819F]/10 px-2.5 py-1 rounded-full whitespace-nowrap flex items-center gap-1"
                        >
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Running...
                        </motion.span>
                      )}
                    </div>
                    
                    <p
                      className={`
                        text-sm mb-1.5 transition-colors
                        ${isActive || isComplete ? 'text-slate-600' : 'text-slate-400'}
                      `}
                    >
                      {step.description}
                    </p>
                    
                    {/* Detail line - only show when active or complete */}
                    {(isActive || isComplete) && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="text-xs text-slate-500 font-mono bg-slate-100 px-2 py-1 rounded mt-2"
                      >
                        {step.detail}
                      </motion.p>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer info */}
        <div className="px-8 pb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Thermometer className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 mb-1">Live Climate Analysis</p>
              <p className="text-blue-700 text-xs">
                We're analyzing real satellite data from your location to calculate accurate system losses. 
                This ensures your analysis reflects actual environmental conditions.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}